import {
  Box,
  Card,
  styled,
  Chip,
  Typography,
  ChipProps,
  useTheme,
} from '@mui/material';

import {BaseSyntheticEvent, useEffect, useRef, useState} from 'react';
import {Tooltip} from '@jobseeker/lotus/elements';
import ButtonV2 from '@jobseeker/lotus/elements/ButtonV2';
import {useAlertCallback} from '@jobseeker/lotus/hooks/alert';
import {openUrl} from '@jobseeker/common-core/utils/common';
import CircleLoader from '@jobseeker/lotus/components/Loader';
import PersonalizeHeader from './PersonalizeHeader';
import PersonalizeSearch from './PersonalizeSearch';
import TagsSkeleton from './TagsSkeleton';

type CustomChipProps = ChipProps & {
  select?: boolean;
  padding?: string;
};

const CustomChip = styled(Chip)<CustomChipProps>(props => ({
  background: props?.select
    ? '#49966E'
    : 'linear-gradient(0deg, rgba(255, 255, 255, 0.70) 0%, rgba(255, 255, 255, 0.70) 100%), #CBD5E1',
  padding: props?.padding,
  width: 'fit-content',
  fontWeight: props?.select ? 500 : 400,
  color: props?.select ? '#fff' : 'primary',
  '.MuiChip-label': {
    padding: '0px',
  },
  '.MuiChip-deleteIcon': {
    marginRight: '0px',
    marginLeft: '2px',
  },
}));

/* eslint-disable @typescript-eslint/no-explicit-any */
const Button = ({tooltipTitle, ...props}: any) => {
  if (tooltipTitle) {
    return (
      <Tooltip title={tooltipTitle} arrow>
        <Box>
          <ButtonV2 {...props} />
        </Box>
      </Tooltip>
    );
  } else {
    return <ButtonV2 {...props} />;
  }
};

type Tag = {id: number; name: string; count?: number; select?: boolean};

type PersonalizeTagSelectionType = {
  currentSelectedTags: Array<Tag>;
  handleTagSelection: (
    name: string,
    tagId: number,
    operation: 'SELECT' | 'DESELECT'
  ) => void;
  handleSearch: (query: string) => void;
  handleSubmitConfirm: () => void;
  selectEnabled: boolean;
  updateCalled: boolean;
  resetTags: () => void;
  selectedCount: number;
  toggleSelectedTagsAlert: () => void;
  categorySelected: boolean;
  isMobile: boolean;
  skip: boolean;
  searchValue: string;
};
const PersonalizeTagSelection = ({
  currentSelectedTags,
  handleTagSelection,
  handleSearch,
  handleSubmitConfirm,
  selectEnabled,
  updateCalled,
  resetTags,
  selectedCount,
  toggleSelectedTagsAlert,
  categorySelected,
  isMobile,
  skip,
  searchValue,
}: PersonalizeTagSelectionType) => {
  const [searchActive, setSearchActive] = useState(false);

  const onFilterChange = (event: BaseSyntheticEvent) => {
    handleSearch(event.target.value ?? '');
    if (event.target.value?.length) {
      setSearchActive(true);
    } else {
      setSearchActive(false);
    }
  };

  const [SkipAlert, toggleSkipAlert] = useAlertCallback({
    primaryCallback: () => {
      console.log('🚀 ~ Cancel:');
    },
    secondaryCallback: () => {
      openUrl('/jobfeed', '_self');
    },
    title: 'Your selected tags will not be saved if you skip',
    subtitle: 'Are you sure you want to skip this step?',
    primaryAction: 'Cancel',
    secondaryAction: 'Confirm',
    useBottomSheet: true,
  });

  useEffect(() => {
    if (!searchActive) {
      resetTags();
    }
  }, [searchActive]);

  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (boxRef.current) {
      console.log(boxRef.current, 'boxref');
    }
  }, [boxRef]);

  const theme = useTheme();

  const ConfirmButton = () => (
    <Button
      variant="contained"
      color="secondary"
      sx={{
        minWidth: {
          xxs: categorySelected && isMobile ? 102 : 149,
          md: 175,
        },
        mr: 2,
      }}
      disabled={!selectEnabled}
      onClick={handleSubmitConfirm}
    >
      {!updateCalled ? (
        <Typography fontSize={{xxs: 14, md: 16}} fontWeight={500}>
          {categorySelected && isMobile ? 'Proceed' : 'Save & Continue'}
        </Typography>
      ) : (
        <CircleLoader sx={{color: 'white'}} size={isMobile ? 14 : 16} />
      )}
    </Button>
  );

  const PersonalizedFooter = () => (
    <Box
      sx={{
        background: 'white',
        width: boxRef.current?.clientWidth || '328px',
        // left:0,
        height: '88px',
        py: 3,
        position: 'fixed',
        bottom: 0,
        mx: 2,
        maxWidth: {xxs: 'calc(100vw - 16px)', md: 1252},
        // right: 0,
        left: {
          xxs: 'auto',
          md: boxRef.current?.getBoundingClientRect()?.x || 100,
        },
        margin: 'auto',
        borderRadius: '16px 16px 0 0',
        boxShadow: '0px 4px 4px 0px #0000000F inset',
        display: {xxs: 'block', md: 'flex'},
        alignItems: 'center',
        justifyContent: {md: 'space-between', lg: 'end'},
      }}
    >
      <Box
        display={{xxs: 'flex', md: 'block'}}
        justifyContent={categorySelected ? 'end' : 'space-between'}
        alignItems={skip ? 'flex-start' : 'center'}
      >
        <Typography
          variant="body1"
          fontWeight={selectedCount > 0 ? 500 : 400}
          color={selectedCount > 0 ? 'primary' : 'text.secondary'}
          ml={2}
          display={{
            xxs: categorySelected ? 'none' : 'block',
            md: 'block',
            lg: 'none',
          }}
          onClick={toggleSelectedTagsAlert}
          sx={{
            cursor: 'pointer',
          }}
          fontSize={{xxs: 14, md: 16}}
        >
          {`Selected Tags (${selectedCount ?? 0})`}
        </Typography>
        {isMobile && <ConfirmButton />}
      </Box>
      <Box
        display={'flex'}
        justifyContent={{xxs: categorySelected ? 'end' : 'space-between'}}
        sx={{
          alignItems: 'center',
        }}
      >
        {skip && (
          <Typography
            variant="body1"
            fontWeight={500}
            color={'primary'}
            ml={2}
            display={{
              xxs: categorySelected ? 'none' : 'block',
              md: 'block',
            }}
            onClick={toggleSkipAlert}
            sx={{
              cursor: 'pointer',
              marginTop: {xxs: -1, md: '0px'},
              mx: {xxs: 2, md: 3},
            }}
            fontSize={{xxs: 14, md: 16}}
          >
            Skip for now
          </Typography>
        )}
        {!isMobile && <ConfirmButton />}
      </Box>
    </Box>
  );

  return (
    <>
      <Card
        ref={boxRef}
        sx={{
          boxShadow: 2,
          borderRadius: 4,
          marginLeft: {md: 2},
        }}
      >
        {categorySelected && (
          <Box
            width="100%"
            overflow={'scroll'}
            sx={{
              // maxHeight: 692,
              py: 3,
              height: 'calc(100vh - 170px)',
            }}
          >
            <Box px={2} display={{xxs: 'none', md: 'block'}}>
              <PersonalizeHeader
                isMobile={isMobile}
                searchValue={searchValue}
                onFilterChange={onFilterChange}
              />
            </Box>

            <Box mt={{xxs: 0, md: 2.5}} px={{xxs: 0, md: 0.5}}>
              {searchActive && (
                <Typography
                  variant="subtitle2"
                  color={'text'}
                  sx={{
                    px: 1.5,
                    mb: {xxs: 1, md: 0},
                  }}
                >
                  Search Results
                </Typography>
              )}
              <Box px={1.5} display={{xxs: 'block', md: 'none'}}>
                {!searchActive && (
                  <Typography variant="subtitle2" mb={1}>
                    Select tags to see jobs that fit your skills and interests{' '}
                  </Typography>
                )}
                <PersonalizeSearch
                  searchValue={searchValue}
                  width="100%"
                  onFilterChange={onFilterChange}
                />
              </Box>
              <Box display={'flex'} flexWrap={'wrap'} mb={2}>
                {currentSelectedTags?.length > 0 ? (
                  currentSelectedTags?.map((tagInfo: Tag, index: number) => {
                    return (
                      <Box
                        ml={'12px'}
                        mt={1.5}
                        key={`${index}_${tagInfo.name}`}
                      >
                        <CustomChip
                          key={`filterchip-filter`}
                          label={tagInfo.name}
                          select={tagInfo.select}
                          padding={`4px 16px 4px 16px`}
                          onClick={() => {
                            handleTagSelection(
                              tagInfo.name,
                              tagInfo.id,
                              tagInfo.select ? 'DESELECT' : 'SELECT'
                            );
                          }}
                          data-testid={`filterchip-filter-${tagInfo.name}`}
                          sx={
                            tagInfo.select
                              ? {
                                  ':hover': {
                                    backgroundColor: theme.palette.primary.main,
                                  },
                                  cursor: 'pointer',
                                }
                              : {cursor: 'pointer'}
                          }
                        />
                      </Box>
                    );
                  })
                ) : !searchActive ? (
                  <Box
                    display={'flex'}
                    flexWrap={'wrap'}
                    width={'100%'}
                    mt={1.5}
                  >
                    <TagsSkeleton skeletonChips={65} />
                  </Box>
                ) : (
                  <Typography
                    px={1.5}
                    mt={{xxs: 1.5, md: 0}}
                    fontSize={{xxs: 14, md: 16}}
                  >
                    Oops! Your search tag might not be listed with us. Please
                    select from listed tags
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
        {(currentSelectedTags?.length > 0 || isMobile) && (
          <PersonalizedFooter />
        )}
      </Card>
      <SkipAlert />
    </>
  );
};

export default PersonalizeTagSelection;
